import { keepPreviousData, useQuery } from "@tanstack/react-query";
import URI from "urijs";
import cloneDeep from "lodash/cloneDeep";
import concat from "lodash/concat";
import pick from "lodash/pick";
import omit from "lodash/omit";
import { qualifyUrl } from "util/URLUtils";
import PaginationURL from "util/PaginationURL";
import UserNotification from "util/UserNotification";
import fetch from "logic/rest/FetchProvider";
import { ConfigurationsActions } from "stores/configurations/ConfigurationsStore";
import { defaultOnError } from "util/conditional/onError";
import FiltersForQueryParams from "components/common/EntityFilters/FiltersForQueryParams";
import { CurrentUserStore } from "stores/users/CurrentUserStore";
import EventDefinitionTagsFilter from "components/event-definitions/EventDefinitionTagsFilter";
import EventDefinitionTypeFilter from "components/event-definitions/EventDefinitionTypeFilter";
export const EVENT_DEFINITIONS_QUERY_KEY = ["event-definitions"];
const sourceUrl = "/events/definitions";
const eventDefinitionsUrl = ({
  segments = [],
  query = {}
}) => {
  const uri = new URI(sourceUrl);
  const nextSegments = concat(uri.segment(), segments);
  uri.segmentCoded(nextSegments);
  uri.query(query);
  return qualifyUrl(uri.resource());
};
const setAlertFlag = (eventDefinition) => {
  var _a;
  const isAlert = ((_a = eventDefinition.notifications) != null ? _a : []).length > 0;
  return { ...eventDefinition, alert: isAlert };
};
const extractSchedulerInfo = (eventDefinition) => {
  const clonedEventDefinition = cloneDeep(eventDefinition);
  const { _is_scheduled } = pick(clonedEventDefinition.config, ["_is_scheduled"]);
  clonedEventDefinition.config = omit(clonedEventDefinition.config, ["_is_scheduled"]);
  return { eventDefinition: clonedEventDefinition, isScheduled: _is_scheduled != null ? _is_scheduled : true };
};
const TAGS_ATTRIBUTE = {
  id: "tags",
  title: "Tags",
  type: "STRING",
  sortable: false,
  searchable: true,
  filterable: true,
  filter_component: EventDefinitionTagsFilter
};
export const fetchEventDefinitions = (searchParams) => {
  CurrentUserStore.update(CurrentUserStore.getInitialState().currentUser.username);
  const url = PaginationURL(`${sourceUrl}/paginated`, searchParams.page, searchParams.pageSize, searchParams.query, {
    sort: searchParams == null ? void 0 : searchParams.sort.attributeId,
    order: searchParams == null ? void 0 : searchParams.sort.direction,
    filters: FiltersForQueryParams(searchParams.filters)
  });
  return fetch("GET", qualifyUrl(url)).then((response) => {
    const { elements, query, attributes, pagination } = response;
    const { count, total, page, per_page: perPage } = pagination;
    return {
      list: elements,
      pagination: {
        count,
        total,
        page,
        perPage,
        query
      },
      // The backend marks `type` (config.type) filterable; attach the custom filter_component
      // here so the dropdown lists plugin-provided type display names instead of a text input.
      attributes: [
        ...(attributes != null ? attributes : []).map(
          (attribute) => attribute.id === "type" ? { ...attribute, filter_component: EventDefinitionTypeFilter } : attribute
        ),
        TAGS_ATTRIBUTE
      ]
    };
  });
};
export const fetchAllEventDefinitions = () => fetch("GET", eventDefinitionsUrl({ query: { per_page: 0 } }));
export const getEventDefinition = (eventDefinitionId) => fetch("GET", eventDefinitionsUrl({ segments: [eventDefinitionId, "with-context"] })).then(
  (response) => ({
    eventDefinition: response.event_definition,
    context: response.context,
    is_mutable: response.is_mutable
  }),
  (error) => {
    if (error.status === 404) {
      UserNotification.error(
        `Unable to find Event Definition with id <${eventDefinitionId}>, please ensure it wasn't deleted.`,
        "Could not retrieve Event Definition"
      );
    }
    throw error;
  }
);
export const createEventDefinition = (newEventDefinition) => {
  const { eventDefinition, isScheduled } = extractSchedulerInfo(newEventDefinition);
  return fetch("POST", eventDefinitionsUrl({ query: { schedule: isScheduled } }), setAlertFlag(eventDefinition)).then(
    (response) => {
      UserNotification.success(
        "Event Definition created successfully",
        `Event Definition "${eventDefinition.title}" was created successfully.`
      );
      return response;
    },
    (error) => {
      if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
        UserNotification.error(
          `Creating Event Definition "${eventDefinition.title}" failed with status: ${error}`,
          "Could not save Event Definition"
        );
      }
      throw error;
    }
  );
};
export const copyEventDefinition = (eventDefinition) => fetch("POST", eventDefinitionsUrl({ segments: [eventDefinition.id, "duplicate"] })).then(
  (response) => {
    UserNotification.success(
      "Event Definition duplicated successfully",
      `Event Definition "${response.title}" was created successfully.`
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        `Duplicating Event Definition "${eventDefinition.title}" failed with status: ${error}`,
        "Could not duplicate Event Definition"
      );
    }
    throw error;
  }
);
export const updateEventDefinition = (eventDefinitionId, updatedEventDefinition) => {
  const { eventDefinition, isScheduled } = extractSchedulerInfo(updatedEventDefinition);
  return fetch(
    "PUT",
    eventDefinitionsUrl({ segments: [eventDefinitionId], query: { schedule: isScheduled } }),
    setAlertFlag(eventDefinition)
  ).then(
    (response) => {
      UserNotification.success(
        "Event Definition updated successfully",
        `Event Definition "${eventDefinition.title}" was updated successfully.`
      );
      return response;
    },
    (error) => {
      if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
        UserNotification.error(
          `Updating Event Definition "${eventDefinition.title}" failed with status: ${error}`,
          "Could not update Event Definition"
        );
      }
      throw error;
    }
  );
};
export const deleteEventDefinition = (eventDefinition) => fetch("DELETE", eventDefinitionsUrl({ segments: [eventDefinition.id] }));
export const enableEventDefinition = (eventDefinition) => fetch("PUT", eventDefinitionsUrl({ segments: [eventDefinition.id, "schedule"] })).then(
  (response) => {
    UserNotification.success(
      "Event Definition successfully enabled",
      `Event Definition "${eventDefinition.title}" was successfully enabled.`
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        `Enabling Event Definition "${eventDefinition.title}" failed with status: ${error}`,
        "Could not enable Event Definition"
      );
    }
    throw error;
  }
);
export const disableEventDefinition = (eventDefinition) => fetch("PUT", eventDefinitionsUrl({ segments: [eventDefinition.id, "unschedule"] })).then(
  (response) => {
    UserNotification.success(
      "Event Definition successfully disabled",
      `Event Definition "${eventDefinition.title}" was successfully disabled.`
    );
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        `Disabling Event Definition "${eventDefinition.title}" failed with status: ${error}`,
        "Could not disable Event Definition"
      );
    }
    throw error;
  }
);
export const clearNotificationQueue = (eventDefinition) => fetch("PUT", eventDefinitionsUrl({ segments: [eventDefinition.id, "clear-notification-queue"] })).then(
  (response) => {
    UserNotification.success("Queued notifications cleared.", "Queued notifications were successfully cleared.");
    return response;
  },
  (error) => {
    if (error.status !== 400 || !error.additional.body || !error.additional.body.failed) {
      UserNotification.error(
        `Clearing queued notifications failed with status: ${error}`,
        "Could not clear queued notifications"
      );
    }
    throw error;
  }
);
export const keyFn = (searchParams) => [
  ...EVENT_DEFINITIONS_QUERY_KEY,
  "overview-paginated",
  searchParams
];
export const useEventDefinitionWithContext = (eventDefinitionId) => {
  const { data, isFetching } = useQuery({
    queryKey: [...EVENT_DEFINITIONS_QUERY_KEY, eventDefinitionId],
    queryFn: () => defaultOnError(
      getEventDefinition(eventDefinitionId),
      "Loading Event Definition failed with status",
      "Could not load Event definition"
    ),
    retry: false
  });
  return {
    data: isFetching ? null : data,
    isFetching
  };
};
export const useAllEventDefinitions = () => useQuery({
  queryKey: [...EVENT_DEFINITIONS_QUERY_KEY, "all"],
  queryFn: fetchAllEventDefinitions
});
const useEventDefinitions = (searchParams, { enabled } = { enabled: true }) => {
  const { data, refetch, isInitialLoading } = useQuery({
    queryKey: [...EVENT_DEFINITIONS_QUERY_KEY, "paginated", searchParams],
    queryFn: () => defaultOnError(
      fetchEventDefinitions(searchParams),
      "Loading Event Definitions failed with status",
      "Could not load Event definition"
    ),
    placeholderData: keepPreviousData,
    retry: false,
    enabled
  });
  return {
    data,
    refetch,
    isInitialLoading
  };
};
export default useEventDefinitions;
export function useGetEntityTypes() {
  const { data, isLoading } = useQuery({
    // Deliberately not under EVENT_DEFINITIONS_QUERY_KEY: this is not event definition
    // entity data, so it should not be refetched on event definition mutations.
    queryKey: ["event-definition-entity-types"],
    queryFn: () => defaultOnError(
      fetch("GET", qualifyUrl("/events/entity_types")),
      "Loading event definition entity types failed with status",
      "Could not load event definition entity types"
    )
  });
  return {
    entityTypes: isLoading ? [] : data || [],
    loadingEntityTypes: isLoading
  };
}
export function useGetListEventsClusterConfig() {
  const { data, isLoading } = useQuery({
    // Deliberately not under EVENT_DEFINITIONS_QUERY_KEY: this is cluster configuration,
    // not event definition entity data, so it should not be refetched on event definition mutations.
    queryKey: ["events-cluster-config"],
    queryFn: () => defaultOnError(
      ConfigurationsActions.listEventsClusterConfig(),
      "Loading event definition list events cluster config failed with status",
      "Could not load event definition list events cluster config"
    )
  });
  return {
    eventsClusterConfig: isLoading ? {} : data || {},
    loadingEventsClusterConfig: isLoading
  };
}
